<template>
  <section>
    <div class="d-flex justify-content-center">
      <b-row class="mt-3 col-8">
        <b-col class="col-12 text-center">
          <h1 class="mb-1">Detalles del talento</h1>
          <p>Actualiza el nombre, correo y foto de perfil del talento de tu agencia.</p>
        </b-col>

        <b-col class="col-12 d-flex align-items-center mt-2 mb-2">
          <b-avatar :src="avatar_preview || current_avatar" size="72" class="mr-1" />
          <div>
            <p class="mb-25 font-weight-bold">Foto de perfil</p>
            <div class="d-flex align-items-center">
              <b-button variant="outline-primary" size="sm" class="mr-50" @click="$refs.avatar_input.click()">
                <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                Cambiar foto
              </b-button>
              <b-button v-if="avatar" variant="flat-danger" size="sm" @click="removeAvatar">
                Quitar
              </b-button>
            </div>
            <small v-if="avatar_error" class="text-danger d-block mt-25">{{ avatar_error }}</small>
            <small v-else class="text-muted d-block mt-25">JPG o PNG, máximo 5MB</small>
            <input
              ref="avatar_input"
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              class="d-none"
              @change="onAvatarChange"
            />
          </div>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Nombre *" label-for="input-talent-first-name">
            <b-form-input id="input-talent-first-name" v-model="first_name" placeholder="Nombre" :state="first_name ? true : null" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Apellido *" label-for="input-talent-last-name">
            <b-form-input id="input-talent-last-name" v-model="last_name" placeholder="Apellido" :state="last_name ? true : null" />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Correo electrónico *" label-for="input-talent-email">
            <b-form-input id="input-talent-email" v-model="email" type="email" placeholder="Correo electrónico" :state="email ? state_email : null" />
            <b-form-invalid-feedback>Ingresa un correo electrónico válido.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <hr class="my-2">
        <b-col class="col-12 d-flex justify-content-center">
          <b-button variant="blue-button-next-step" class="blue-button-next-step my-2" :disabled="!can_save" @click="save()">
            <b-spinner v-if="saving" small class="mr-50" />
            {{ $t('creator.savePreview') }}
          </b-button>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BAvatar,
  BSpinner,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import talentosService from '@/services/talentos'
import { verifyAsset } from '@/libs/utils/urls'

const AVATAR_EXTS = ['jpg', 'jpeg', 'png']
const AVATAR_MAX_SIZE = 5 * 1024 * 1024

export default {
  name: 'talentDetails',
  components: {
    BRow,
    BCol,
    BButton,
    BAvatar,
    BSpinner,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
  },
  props: {
    details: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      first_name: this.details.first_name || '',
      last_name: this.details.last_name || '',
      email: this.details.email || '',
      avatar: null,
      avatar_preview: '',
      avatar_error: '',
      saving: false,
    }
  },
  computed: {
    current_avatar() {
      return this.details.avatar ? verifyAsset(this.details.avatar) : ''
    },
    state_email() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())
    },
    can_save() {
      return !this.saving && !!this.first_name.trim() && !!this.last_name.trim() && this.state_email
    },
  },
  beforeDestroy() {
    if (this.avatar_preview) URL.revokeObjectURL(this.avatar_preview)
  },
  methods: {
    onAvatarChange(e) {
      const file = e.target.files[0]
      this.$refs.avatar_input.value = ''
      if (!file) return

      const ext = file.name.split('.').pop().toLowerCase()
      const valid_type = ['image/jpeg', 'image/png'].includes(file.type) && AVATAR_EXTS.includes(ext)
      if (!valid_type) {
        this.avatar_error = 'Solo se permiten imágenes JPG o PNG.'
        return
      }
      if (file.size > AVATAR_MAX_SIZE) {
        this.avatar_error = 'La imagen no debe pesar más de 5MB.'
        return
      }

      this.removeAvatar()
      this.avatar = file
      this.avatar_preview = URL.createObjectURL(file)
    },
    removeAvatar() {
      if (this.avatar_preview) URL.revokeObjectURL(this.avatar_preview)
      this.avatar = null
      this.avatar_preview = ''
      this.avatar_error = ''
    },
    getBackendError(error) {
      const data = error && error.response && error.response.data
      if (!data) return error && error.message ? error.message : ''
      const message = data.response && data.response.message !== undefined ? data.response.message : (data.detail || data.message || data)
      if (typeof message === 'string') return message
      if (Array.isArray(message)) return message.join(', ')
      if (message && typeof message === 'object') {
        return Object.entries(message)
          .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join(' | ')
      }
      return ''
    },
    async save() {
      if (!this.can_save) return
      this.saving = true

      const fd = new FormData()
      fd.append('first_name', this.first_name.trim())
      fd.append('last_name', this.last_name.trim())
      fd.append('email', this.email.trim())
      if (this.avatar) fd.append('avatar', this.avatar)

      try {
        const response = await talentosService.editTalent(this.details.id, fd)
        const updated = response && typeof response === 'object' ? response : {}
        this.$emit('update_proposal', {
          ...this.details,
          first_name: updated.first_name || this.first_name.trim(),
          last_name: updated.last_name || this.last_name.trim(),
          email: updated.email || this.email.trim(),
          avatar: updated.avatar || this.details.avatar,
        })
      } catch (e) {
        const backend_error = this.getBackendError(e)
        const message = 'No se pudieron guardar los detalles del talento.'
        this.$bvToast.toast(backend_error ? `${message} (${backend_error})` : message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
